import React, { useState } from "react";
import useSEO from "../hooks/useSEO";
import Reveal from "../components/Reveal";
import { Arrow, Mail, Phone, Pin, Clock } from "../components/Icons";
import { PageHead, FAQ } from "../components/Shared";
import { faqs } from "../data/content";

const EMAIL = "future@victrie.com";
const PHONE = "+254713199322";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", service: "AI & Machine Learning", budget: "", message: "" });
  const [status, setStatus] = useState(null); // null | 'sending' | 'ok' | 'err'

  useSEO({
    title: "Contact Victrie — Start Your Software Project",
    description: "Contact Victrie, Nairobi's futuristic software development company. Email future@victrie.com or call +254 713 199 322 to discuss AI, web, mobile and cloud projects.",
    path: "/contact",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      name: "Contact Victrie",
      url: "https://victrie.com/contact",
      mainEntity: { "@id": "https://victrie.com/#organization" },
    },
  });

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    const body = `Name: ${form.name}\nEmail: ${form.email}\nCompany: ${form.company}\nService: ${form.service}\nBudget: ${form.budget}\n\n${form.message}`;
    try {
      const res = await fetch(`https://formsubmit.co/ajax/${EMAIL}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ ...form, _subject: `New project inquiry from ${form.name}`, _template: "table" }),
      });
      if (!res.ok) throw new Error("bad response");
      setStatus("ok");
    } catch {
      // Fallback: open the visitor's email client with everything pre-filled.
      window.location.href = `mailto:${EMAIL}?subject=${encodeURIComponent(`Project inquiry from ${form.name}`)}&body=${encodeURIComponent(body)}`;
      setStatus("err");
    }
  };

  return (
    <>
      <PageHead
        crumb="Contact"
        title={<>Let's build the <span className="grad-text">future</span> together</>}
        text="Tell us about your project. A senior engineer, not a sales bot, will reply within one business day."
      />

      <section className="section" style={{ paddingTop: 0 }}>
        <div className="container contact-grid">
          <Reveal className="contact-info">
            <a className="card" href={`mailto:${EMAIL}`}>
              <div className="icon-badge"><Mail /></div>
              <div><small>Email us</small><strong>{EMAIL}</strong></div>
            </a>
            <a className="card" href={`tel:${PHONE}`}>
              <div className="icon-badge"><Phone /></div>
              <div><small>Call / WhatsApp</small><strong>+254 713 199 322</strong></div>
            </a>
            <div className="card">
              <div className="icon-badge"><Pin /></div>
              <div><small>Headquarters</small><strong>Nairobi, Kenya</strong></div>
            </div>
            <div className="card">
              <div className="icon-badge"><Clock /></div>
              <div><small>Working hours</small><strong>Mon – Fri, 8:00 – 18:00 EAT</strong></div>
            </div>
            <a className="btn btn--ghost" href={`https://wa.me/${PHONE.replace("+", "")}?text=${encodeURIComponent("Hi Victrie, I'd like to discuss a project.")}`} target="_blank" rel="noopener noreferrer" style={{ justifyContent: "center" }}>
              Chat on WhatsApp <Arrow />
            </a>
          </Reveal>

          <Reveal delay={120} className="glass form" as="form" onSubmit={submit}>
            <h3 style={{ marginBottom: 4 }}>Start a project</h3>
            <p style={{ marginBottom: 6, fontSize: "0.95rem" }}>Fields marked * are required.</p>
            <div className="form__row">
              <div className="field"><label htmlFor="name">Full name *</label><input id="name" required value={form.name} onChange={update("name")} placeholder="Jane Doe" /></div>
              <div className="field"><label htmlFor="email">Work email *</label><input id="email" type="email" required value={form.email} onChange={update("email")} placeholder="jane@company.com" /></div>
            </div>
            <div className="form__row">
              <div className="field"><label htmlFor="company">Company</label><input id="company" value={form.company} onChange={update("company")} placeholder="Company Ltd" /></div>
              <div className="field">
                <label htmlFor="service">Service needed</label>
                <select id="service" value={form.service} onChange={update("service")}>
                  {["AI & Machine Learning", "Custom Software", "Web Application", "Mobile App", "Cloud & DevOps", "UI/UX Design", "Data Engineering", "Cybersecurity", "Not sure yet"].map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div className="field">
              <label htmlFor="budget">Estimated budget</label>
              <select id="budget" value={form.budget} onChange={update("budget")}>
                <option value="">Select a range</option>
                {["Under $5,000", "$5,000 – $20,000", "$20,000 – $60,000", "$60,000 – $150,000", "$150,000+"].map((b) => <option key={b}>{b}</option>)}
              </select>
            </div>
            <div className="field"><label htmlFor="message">Tell us about your project *</label><textarea id="message" required value={form.message} onChange={update("message")} placeholder="What are you building? Who is it for? What does success look like?" /></div>
            {status === "ok" && <div className="form__status ok">Thank you! Your message is on its way. We will reply within one business day.</div>}
            {status === "err" && <div className="form__status err">We opened your email client with your message pre-filled. If nothing happened, email us directly at {EMAIL}.</div>}
            <button className="btn btn--primary btn--lg" type="submit" disabled={status === "sending"} style={{ justifyContent: "center" }}>
              {status === "sending" ? "Sending…" : "Send message"} <Arrow />
            </button>
            <p style={{ fontSize: "0.8rem", color: "var(--muted)", margin: 0 }}>By submitting, you agree to our privacy policy. We never share your information.</p>
          </Reveal>
        </div>
      </section>

      <section className="section" style={{ paddingTop: 0 }}>
        <div className="container">
          <Reveal>
            <iframe
              className="map-embed"
              title="Victrie location — Nairobi, Kenya"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              src="https://www.google.com/maps?q=Nairobi,Kenya&z=12&output=embed"
            />
          </Reveal>
        </div>
      </section>

      <section className="section" style={{ paddingTop: 0 }}>
        <div className="container">
          <Reveal className="sec-head center"><span className="eyebrow">Before you reach out</span><h2>Common questions</h2></Reveal>
          <FAQ items={faqs.slice(1, 6)} />
        </div>
      </section>
    </>
  );
}
